/**
 * @fileoverview Main onboarding modal container that orchestrates the signup/signin flow.
 * Manages step navigation and renders appropriate content based on auth mode.
 * @module components/onboarding/OnboardingModal
 */

"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useOnboardingState } from "@/hooks/useOnboardingState";
import { trackOnboarding } from "@/lib/telemetry";
import { cn } from "@/lib/utils";
import { AccountCreation } from "./steps/AccountCreation";
import { PlanConfirmation } from "./steps/PlanConfirmation";
import { SignIn } from "./steps/SignIn";

/**
 * Main onboarding modal that handles user authentication flows.
 *
 * @description This component serves as the container for both sign-up and sign-in flows.
 * It renders different content based on the current auth mode and step:
 *
 * Sign-up flow (authMode === "signup"):
 * 1. PlanConfirmation - User confirms their selected plan
 * 2. AccountCreation - User creates their account with email/password or Google
 *
 * Sign-in flow (authMode === "signin"):
 * - SignIn - User signs in with existing credentials
 *
 * Features:
 * - Smooth entrance/exit animations with scale and opacity transitions
 * - Backdrop blur overlay for focus
 * - Step indicator dots for signup flow (shows progress through steps)
 * - Accessible dialog with screen reader titles/descriptions
 * - Close button with consistent styling
 * - Managed visibility through onboarding state hook
 *
 * @returns Onboarding modal element, or null when not open
 *
 * @example
 * // Render at app root level - modal manages its own visibility
 * function App() {
 *   return (
 *     <>
 *       <OnboardingModal />
 *       <MainContent />
 *     </>
 *   );
 * }
 *
 * @example
 * // Opening the modal is controlled via useOnboardingState hook
 * function SignUpButton() {
 *   const { actions } = useOnboardingState();
 *   return (
 *     <button onClick={() => actions.openModal()}>
 *       Get Started
 *     </button>
 *   );
 * }
 */
export function OnboardingModal() {
  const { state, actions } = useOnboardingState();
  const [isVisible, setIsVisible] = useState(false);
  const visibilityFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (visibilityFrameRef.current !== null) {
      cancelAnimationFrame(visibilityFrameRef.current);
      visibilityFrameRef.current = null;
    }

    if (state.isModalOpen) {
      visibilityFrameRef.current = requestAnimationFrame(() => {
        visibilityFrameRef.current = null;
        setIsVisible(true);
      });
    } else {
      setIsVisible(false);
    }

    return () => {
      if (visibilityFrameRef.current !== null) {
        cancelAnimationFrame(visibilityFrameRef.current);
        visibilityFrameRef.current = null;
      }
    };
  }, [state.isModalOpen]);

  // Track when modal opens
  useEffect(() => {
    if (state.isModalOpen) {
      // Determine source based on context
      // Default to 'cta' as it's the most common case
      // This could be enhanced by passing source through state
      const source: "homepage" | "pricing" | "cta" = "cta";
      trackOnboarding.started(source);
    }
  }, [state.isModalOpen]);

  const handleClose = () => {
    actions.closeModal();
  };

  if (!state.isModalOpen) {
    return null;
  }

  return (
    <DialogPrimitive.Root onOpenChange={handleClose} open={state.isModalOpen}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/40 backdrop-blur-sm data-[state=closed]:animate-out data-[state=open]:animate-in" />
        <DialogPrimitive.Content
          className={cn(
            "fixed top-[50%] left-[50%] z-50 w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] transition-all duration-300 sm:max-w-2xl",
            isVisible ? "scale-100 opacity-100" : "translate-y-[-48%] scale-95 opacity-0",
          )}
        >
          <DialogPrimitive.Title className="sr-only">
            {state.authMode === "signin"
              ? "Sign In"
              : state.modalStep === "plan"
                ? "Choose Your Plan"
                : "Create Your Account"}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            {state.authMode === "signin"
              ? "Sign in to your Aideco account"
              : state.modalStep === "plan"
                ? "Select a plan to get started with Aideco"
                : "Create your account to continue"}
          </DialogPrimitive.Description>
          <div className="relative rounded-lg border border-gray-200 bg-white shadow-2xl">
            <DialogPrimitive.Close
              className="pointer-events-auto absolute top-6 right-6 z-20 rounded-full p-2 text-gray-500 transition-all hover:bg-gray-100 hover:text-gray-900 focus:outline-hidden focus:ring-2 focus:ring-gold-accent focus:ring-offset-2 focus:ring-offset-white"
              onClick={handleClose}
            >
              <X className="h-5 w-5" strokeWidth={1.5} />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>

            <div className="px-8 py-12 sm:px-12 sm:py-16">
              {state.authMode === "signin" ? (
                <SignIn />
              ) : (
                <>
                  {state.modalStep === "plan" && <PlanConfirmation />}
                  {state.modalStep === "signup" && <AccountCreation />}
                </>
              )}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
